import { useEffect, useMemo, useState } from 'react';
import { cloneDeep, isFinite } from 'lodash';
import { toast } from 'material-react-toastify';
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import { ProductPopularity } from '../../interfaces/PopularProduct/ProductPopularity';
import {
  getProductPopularities,
  updateHighlightedProductOrders,
} from '../../api/PopularProduct';
import { ProductPopularityEntry } from '../../interfaces/PopularProduct/ProductPopularityEntry';
import { sharedColors, sharedStyles } from '../../utils/Style';
import { Waiting } from '../../components/Waiting/Waiting';
import { ProductPopularityChip } from '../PopularProduct/ProductPopularityChip';

export const HighlightedProductsPage = () => {
  const [productPopularities, setProductPopularities] = useState<
    ProductPopularity[]
  >([]);
  const [initialPopularities, setInitialPopularities] = useState<
    ProductPopularity[]
  >([]);
  const [loading, setLoading] = useState(false);

  const availableOptions = useMemo(() => {
    const alreadySelectedIDs = productPopularities.map((p) => p.id);

    return initialPopularities.filter(
      (p) => !alreadySelectedIDs.includes(p.id),
    );
  }, [initialPopularities, productPopularities]);

  const refreshPopularities = () => {
    setLoading(true);
    getProductPopularities()
      .then((popularities) => {
        setInitialPopularities(popularities);
        setProductPopularities(
          popularities
            .filter((p) => isFinite(p.highlightedProductOrder))
            .toSorted(
              (a, b) => a.highlightedProductOrder! - b.highlightedProductOrder!,
            ),
        );
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshPopularities();
  }, []);

  const handleDelete = (id: number) => {
    setProductPopularities(productPopularities.filter((p) => p.id !== id));
  };

  const handleMoveLeft = (index: number) => {
    const newPopularities = cloneDeep(productPopularities);
    newPopularities[index - 1] = productPopularities[index];
    newPopularities[index] = productPopularities[index - 1];

    setProductPopularities(newPopularities);
  };

  const handleMoveRight = (index: number) => {
    const newPopularities = cloneDeep(productPopularities);
    newPopularities[index + 1] = productPopularities[index];
    newPopularities[index] = productPopularities[index + 1];

    setProductPopularities(newPopularities);
  };

  const handleSelect = (entry: ProductPopularity) => {
    setProductPopularities([...productPopularities, entry]);
  };

  const handleSubmit = () => {
    const existingIDs = productPopularities.map((p) => p.id);

    const entries: ProductPopularityEntry[] = [
      ...existingIDs.map((id, i) => ({ id, order: i })),
      ...initialPopularities
        .filter((p) => !existingIDs.includes(p.id))
        .map((p) => ({ id: p.id })),
    ];

    setLoading(true);
    updateHighlightedProductOrders(entries)
      .then(() => {
        toast.success('Haftanın öne çıkan ürünleri başarıyla güncellendi');
        refreshPopularities();
      })
      .catch((err) => {
        toast.error(err.message);
        setLoading(false);
      });
  };

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      <Waiting open={loading} />
      <Typography variant='h5' sx={{ ...sharedStyles.pageTitle }}>
        Haftanın Öne Çıkan Ürünleri
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        {productPopularities.map((p, i) => (
          <ProductPopularityChip
            key={p.id}
            entry={p}
            onMoveRight={
              i < productPopularities.length - 1
                ? () => handleMoveRight(i)
                : undefined
            }
            onMoveLeft={i > 0 ? () => handleMoveLeft(i) : undefined}
            onDelete={() => handleDelete(p.id)}
          />
        ))}
      </Box>
      <Autocomplete
        renderInput={(params) => (
          <TextField
            {...params}
            label='Ürün'
            variant='outlined'
            sx={{ width: 350, mt: 1.5, backgroundColor: sharedColors.white }}
          />
        )}
        options={availableOptions}
        getOptionLabel={(option: ProductPopularity) => option.name}
        value={null}
        onChange={(_, p) => {
          if (p) {
            handleSelect(p);
          }
        }}
      />
      <Button
        variant='contained'
        onClick={handleSubmit}
        sx={{ ...sharedStyles.buttonText, mt: 1.5 }}
      >
        Kaydet
      </Button>
    </Box>
  );
};
